/* eslint-disable react-refresh/only-export-components */
// 同文件导出组件 + 辅助函数为有意模式;react-refresh 仅关注 HMR,不影响运行时。
// 陪练场景创建/编辑 —— 2026-06-05 重构:mode-first 两步 + AI 优先 + 多人设卡片。
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  GraduationCap,
  Loader2,
  MessagesSquare,
  Mic,
  Plus,
  Sparkles,
  Trash2,
  Volume2,
} from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Textarea } from "@/shared/ui/textarea";
import { Label } from "@/shared/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
// C 批返工(2026-09-08 老板「选择弹框用了系统默认」):客户口音/说话风格换 shadcn Select。
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { TagSelector } from "@/entities/tag";
import { Checkbox } from "@/shared/ui/checkbox";
import {
  type ScenarioCreateRequest,
  type ScenarioDimension,
  type ScenarioAiConfig,
  coachingService,
} from "../../api/coachingService";
import { promptPresetService } from "../../api/promptPresetService";
import { productService } from "@/entities/product";
// 声音 V2 词表(与数字人线同一份;放 entities/dh-profile,features 互不 import)。
import { DH_VOICE_DIALECTS, DH_VOICE_STYLES } from "@/entities/dh-profile/model/voiceV2Options";
import {
  dhProfileService,
  voiceProfileSupportsTuning,
} from "@/entities/dh-profile/api/dhProfileService";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";
import { base64ToBlob, normalizeDifficulty } from "./helpers";

const WEIGHT_TOLERANCE = 0.001;
const PERSONA_SEP = "\n---\n"; // 内部存储分隔符(用户看不到,卡片化呈现)

// 多 persona 存成单字符串(\n---\n 分隔)与卡片数组互转。
const splitPersonas = (s: string): string[] =>
  s
    ? s
        .split(/\n\s*-{3,}\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
    : [];
const joinPersonas = (arr: string[]): string =>
  arr
    .map((p) => p.trim())
    .filter(Boolean)
    .join(PERSONA_SEP);

const SCENARIO_GOALS = [
  "售前异议",
  "Demo邀约",
  "售后安抚",
  "续费挽留",
  "投诉处理",
  "竞品对比",
  "价格谈判",
];

/** 音色下拉里「我的音色」项的值前缀(预置音色 id 不会以它开头)。 */
const MY_VOICE_PREFIX = "my:";
/** 「我的音色」试听台词:像客户开口,30 字内(数字人线试听截断上限)。 */
const MY_VOICE_PREVIEW_TEXT = "你们这个到底好在哪?我再比较比较。";

export type ScenarioFormData = {
  name: string;
  difficulty: string;
  mode: string;
  interactionMode: string; // V29:"text" | "voice"(语音对练场景 H5 走全语音半双工)
  description: string;
  maxRounds: string;
  positions: string;
  customerPersona: string;
  productContext: string;
  knowledgeTags: string;
  dimensions: ScenarioDimension[];
  kbWeight: number;
  aiConfig: ScenarioAiConfig;
  customerRoleKey: string | null;
  customerToneKey: string | null;
  status: string;
  productCodes: string[];
  /** 声音 V2(2026-09-08):客户口音(方言 slug,null=普通话)/说话风格。词表与数字人线同源。 */
  voiceDialect: string | null;
  voiceStyle: string | null;
};

/** 存量场景里遗留的模型旋钮(界面已不再提供):有值才原样回带,保存时不被整体替换洗掉。 */
const LEGACY_AI_KEYS = ["model", "evaluatorModel", "temperature", "maxTokens"] as const;
function pickLegacyAiConfig(cfg: ScenarioAiConfig): Partial<ScenarioAiConfig> {
  const out: Partial<ScenarioAiConfig> = {};
  for (const k of LEGACY_AI_KEYS) {
    const v = cfg[k];
    if (v != null && v !== "") (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export const EMPTY_FORM: ScenarioFormData = {
  name: "",
  difficulty: "medium",
  mode: "roleplay",
  interactionMode: "text",
  description: "",
  maxRounds: "8",
  positions: "",
  customerPersona: "",
  productContext: "",
  knowledgeTags: "",
  dimensions: [],
  kbWeight: 0.3,
  aiConfig: {
    voice: null,
    staffAware: false,
  },
  customerRoleKey: null,
  customerToneKey: null,
  status: "active",
  productCodes: [],
  voiceDialect: null,
  voiceStyle: null,
};

// mode 决定必填集。qa_drill 的考核范围(产品线/标签)全可选 —— 字段标签即「不选=通用」:
// 不选时 ai-service 退化到全库 KB 出题(build_coaching_scope 无 productCodes → None → 全库;
// list_chunks_for_quiz 无 tags → 全库兜底)。新租户既无产品线(平台运营维护)也无标签(标签管理只读),
// 强制必选会让 qa_drill 对新租户完全不可建,故不校验。roleplay 描述/人设可空(留空走 AI 生成),
// 仅当填了才校验下限。人设走卡片状态,不进 zod。
const scenarioFormSchema = z
  .object({
    name: z.string().trim().min(1, "场景名称必填"),
    difficulty: z.string(),
    mode: z.enum(["roleplay", "qa_drill"]),
    description: z.string().trim().max(300, "场景描述最多 300 字"),
    maxRounds: z.string(),
    positions: z.string(),
    knowledgeTags: z.string(),
    kbWeight: z.number().min(0).max(1),
    customerRoleKey: z.string().nullable(),
    customerToneKey: z.string().nullable(),
    status: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.mode !== "qa_drill") {
      const desc = val.description.trim();
      if (desc.length > 0 && desc.length < 30) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["description"],
          message: "要么留空(用 AI 生成),要么写够 30 字让 AI 客户理解场景背景",
        });
      }
    }
  });

type ScenarioFormValues = z.infer<typeof scenarioFormSchema>;

const selectCls =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export function ScenarioFormDialog({
  open,
  onClose,
  initial,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  initial: ScenarioFormData;
  onSubmit: (data: ScenarioCreateRequest) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("coaching");
  const isEdit = !!initial.name;

  const form = useForm<ScenarioFormValues>({
    resolver: zodResolver(scenarioFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: initial.name,
      difficulty: initial.difficulty,
      mode: (initial.mode as "roleplay" | "qa_drill") ?? "roleplay",
      description: initial.description,
      maxRounds: initial.maxRounds,
      positions: initial.positions,
      knowledgeTags: initial.knowledgeTags,
      kbWeight: initial.kbWeight,
      customerRoleKey: initial.customerRoleKey,
      customerToneKey: initial.customerToneKey,
      status: initial.status,
    },
  });

  const [dimensions, setDimensions] = useState<ScenarioDimension[]>(initial.dimensions);
  const [aiConfig, setAiConfig] = useState<ScenarioAiConfig>(initial.aiConfig);
  // V29 语音陪练:对练形式(场景级,员工端不可切换)+ 试听播放态
  const [interactionMode, setInteractionMode] = useState<string>(initial.interactionMode);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  // 声音 V2(2026-09-08):客户口音/说话风格。当前系统音色不支持方言 —— 字段照存
  // (B 批已加列,即将随克隆客户声线生效),与 interactionMode 同款卡外 state,不进 zod。
  const [voiceDialect, setVoiceDialect] = useState<string | null>(initial.voiceDialect);
  const [voiceStyle, setVoiceStyle] = useState<string | null>(initial.voiceStyle);
  const [productCodes, setProductCodes] = useState<string[]>(initial.productCodes ?? []);
  // 产品线下拉:收起时一行,展开带搜索(平铺 15 条会把必填的场景名称挤出首屏)
  const [productOpen, setProductOpen] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  // 客户人设卡片(roleplay)。从存量单字符串拆出;提交时合回 \n---\n。
  const [personas, setPersonas] = useState<string[]>(splitPersonas(initial.customerPersona));
  const [personaCount, setPersonaCount] = useState(3);
  // 第一步:选模式(新建时);编辑时直接进表单(模式锁定)。
  const [step, setStep] = useState<"mode" | "form">(isEdit ? "form" : "mode");
  const [scenarioGoal, setScenarioGoal] = useState("售前异议");
  const [generatingScenario, setGeneratingScenario] = useState(false);
  const [generatingPersonas, setGeneratingPersonas] = useState(false);

  const { data: products = [] } = useQuery({
    queryKey: ["product-nodes"],
    queryFn: productService.list,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (open) {
      setProductCodes(initial.productCodes ?? []);
      setProductOpen(false);
      setProductQuery("");
      setPersonas(splitPersonas(initial.customerPersona));
      setPersonaCount(3);
      setStep(initial.name ? "form" : "mode");
      setScenarioGoal("售前异议");
      form.reset({
        name: initial.name,
        difficulty: initial.difficulty,
        mode: (initial.mode as "roleplay" | "qa_drill") ?? "roleplay",
        description: initial.description,
        maxRounds: initial.maxRounds,
        positions: initial.positions,
        knowledgeTags: initial.knowledgeTags,
        kbWeight: initial.kbWeight,
        customerRoleKey: initial.customerRoleKey,
        customerToneKey: initial.customerToneKey,
        status: initial.status,
      });
      setDimensions(initial.dimensions);
      setAiConfig(initial.aiConfig);
      setInteractionMode(initial.interactionMode ?? "text");
      setVoiceDialect(initial.voiceDialect ?? null);
      setVoiceStyle(initial.voiceStyle ?? null);
    }
  }, [open, initial, form]);

  const customerRoleKey = form.watch("customerRoleKey");
  const mode = form.watch("mode");
  const isRoleplay = mode !== "qa_drill";

  const { data: customerRoles } = useQuery({
    queryKey: ["prompt-presets", "peilian_customer", "role"],
    queryFn: () => promptPresetService.list("peilian_customer", "role"),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const { data: customerTones } = useQuery({
    queryKey: ["prompt-presets", "peilian_customer", "tone"],
    queryFn: () => promptPresetService.list("peilian_customer", "tone"),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const { data: presets } = useQuery({
    queryKey: ["coaching", "dimension-presets"],
    queryFn: () => coachingService.listDimensionPresets(),
    enabled: open,
    staleTime: 60 * 60_000,
  });
  // V29 语音陪练:音色小表(全局元数据,长缓存)
  const { data: voicesData } = useQuery({
    queryKey: ["coaching", "voices"],
    queryFn: () => coachingService.listVoices(),
    enabled: open,
    staleTime: 60 * 60_000,
  });
  const voices = voicesData?.voices ?? [];
  // 1024:分身舱「我的音色」—— 只列新版克隆声纹(旧版认不了方言,陪练也合不出它)。
  const { data: myVoiceData } = useQuery({
    queryKey: ["coaching", "my-voices"],
    queryFn: () => dhProfileService.listVoiceProfiles(),
    enabled: open && interactionMode === "voice",
    staleTime: 60_000,
  });
  const myVoices = (myVoiceData?.profiles ?? []).filter(voiceProfileSupportsTuning);
  const usingMyVoice = aiConfig.voiceProfileId != null;
  const voiceValue = usingMyVoice
    ? `${MY_VOICE_PREFIX}${aiConfig.voiceProfileId}`
    : (aiConfig.voice ?? voices[0]?.id ?? "");
  const onVoiceChange = (v: string) => {
    if (v.startsWith(MY_VOICE_PREFIX)) {
      setAiConfig({ ...aiConfig, voiceProfileId: Number(v.slice(MY_VOICE_PREFIX.length)) });
      return;
    }
    if (!v) return; // Radix 在选项还没加载时会回调空值,别当成用户换音色
    // 从「我的音色」换回预置音色:方言 / 风格对它不生效,一并清掉,免得表单上留着假装生效的值。
    // 预置之间切换不动(旧场景存过的口音照旧回显,由用户自己改回普通话)。
    if (aiConfig.voiceProfileId != null) {
      setVoiceDialect(null);
      setVoiceStyle(null);
    }
    setAiConfig({ ...aiConfig, voice: v, voiceProfileId: null });
  };

  const handlePreviewVoice = async () => {
    const voice = aiConfig.voice || voices[0]?.id;
    if ((!voice && !usingMyVoice) || previewingVoice) return;
    setPreviewingVoice(true);
    try {
      let url: string;
      let release = () => {};
      if (usingMyVoice) {
        const res = await coachingService.previewMyVoice({
          voiceProfileId: aiConfig.voiceProfileId as number,
          text: MY_VOICE_PREVIEW_TEXT,
          voiceDialect,
          voiceStyle: voiceStyle ?? "natural",
        });
        url = res.audioUrl;
      } else {
        const res = await coachingService.previewVoice(voice as string);
        url = URL.createObjectURL(base64ToBlob(res.audioBase64, res.mime));
        release = () => URL.revokeObjectURL(url);
      }
      const audio = new Audio(url);
      audio.addEventListener("ended", release, { once: true });
      audio.addEventListener("error", release, { once: true });
      try {
        await audio.play();
      } catch (e) {
        release();
        throw e;
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "试听失败",
        description: errorToast(e).description,
      });
    } finally {
      setPreviewingVoice(false);
    }
  };

  const selectedRoleIncompat = customerRoles?.presets.find(
    (r) => r.key === customerRoleKey
  )?.incompatible;

  const weightSum = dimensions.reduce((s, d) => s + (d.weight || 0), 0);
  const weightOk = dimensions.length === 0 || Math.abs(weightSum - 1.0) < WEIGHT_TOLERANCE;

  const applyPreset = (presetKey: string) => {
    const p = presets?.find((x) => x.presetKey === presetKey);
    if (p) {
      setDimensions(
        p.dimensions.map((d, i) => ({
          dimKey: d.dimKey,
          dimLabel: d.dimLabel,
          weight: typeof d.weight === "string" ? parseFloat(d.weight) : d.weight,
          maxScore: d.maxScore ?? 20,
          displayOrder: d.displayOrder ?? i + 1,
        }))
      );
    }
  };
  const updateDim = (i: number, field: keyof ScenarioDimension, value: unknown) =>
    setDimensions((prev) => prev.map((d, j) => (j === i ? { ...d, [field]: value } : d)));
  const removeDim = (i: number) => setDimensions((prev) => prev.filter((_, j) => j !== i));
  const addDim = () =>
    setDimensions((prev) => [
      ...prev,
      { dimKey: "", dimLabel: "", weight: 0, maxScore: 20, displayOrder: prev.length + 1 },
    ]);
  const toggleProduct = (code: string) =>
    setProductCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  // ── 人设卡片 ────────────────────────────────────────────────
  const updatePersona = (i: number, v: string) =>
    setPersonas((prev) => prev.map((p, j) => (j === i ? v : p)));
  const removePersona = (i: number) => setPersonas((prev) => prev.filter((_, j) => j !== i));
  const addPersona = () => setPersonas((prev) => [...prev, ""]);

  const chooseMode = (m: "roleplay" | "qa_drill") => {
    form.setValue("mode", m);
    setStep("form");
  };

  // ── AI 生成 ────────────────────────────────────────────────
  const handleGenerateScenario = async () => {
    setGeneratingScenario(true);
    try {
      const res = await coachingService.generateScenario({
        productCodes,
        goal: scenarioGoal,
        difficulty: form.getValues("difficulty"),
        knowledgeTags: form.getValues("knowledgeTags"),
      });
      if (res) {
        form.setValue("mode", "roleplay");
        if (res.name) form.setValue("name", res.name);
        if (res.description)
          form.setValue("description", res.description, { shouldValidate: true });
        if (res.customerPersona) setPersonas(splitPersonas(res.customerPersona));
        if (res.difficulty)
          form.setValue("difficulty", normalizeDifficulty(res.difficulty) ?? "medium");
        const presetByType: Record<string, "sales" | "aftersales" | "consult"> = {
          销售类: "sales",
          售后类: "aftersales",
          咨询类: "consult",
        };
        const preset = presetByType[res.scenarioType];
        if (preset) applyPreset(preset);
      }
    } catch (e) {
      // COA-SCN-008:失败必须可见,否则用户看着像「点击没反应」。
      toast({
        variant: "destructive",
        title: "AI 生成失败",
        description: errorToast(e).description,
      });
    } finally {
      setGeneratingScenario(false);
    }
  };

  const handleGeneratePersonas = async () => {
    setGeneratingPersonas(true);
    try {
      const res = await coachingService.generatePersonas({
        productCodes,
        customerRoleKey: form.getValues("customerRoleKey"),
        difficulty: form.getValues("difficulty"),
        scenarioName: form.getValues("name"),
        knowledgeTags: form.getValues("knowledgeTags"),
        count: personaCount,
      });
      if (res?.personas?.length) setPersonas(res.personas);
      else toast({ title: "未生成人设", description: "AI 未返回结果,请重试" });
    } catch (e) {
      // COA-SCN-008:失败必须可见(原 try/finally 无 catch,错误被吞 → 「点击没反应」)。
      toast({
        variant: "destructive",
        title: "AI 生成失败",
        description: errorToast(e).description,
      });
    } finally {
      setGeneratingPersonas(false);
    }
  };

  const handleSubmit = (values: ScenarioFormValues) => {
    if (!weightOk) return;
    // V29:语音对练是 roleplay 专属(qa_drill 打字答题,语音无意义);语音场景音色落 aiConfig.voice
    const isVoice = values.mode !== "qa_drill" && interactionMode === "voice";
    const submitVoice = isVoice ? aiConfig.voice || voices[0]?.id || null : null;
    const submitProfile = isVoice ? (aiConfig.voiceProfileId ?? null) : null;
    // OEM H3(2026-10-01):模型/评估模型/温度/长度上限不再对客开放(ai 侧由 LLM 配置中心按功能统一定,
    // api 也从不透传这四项)。api update 对 aiConfig 是「非 null 即整体替换」,所以库里存量旧值
    // 只原样回带、绝不由界面产生;新建场景不会带出这四个键。
    const legacyAiCfg = pickLegacyAiConfig(aiConfig);
    const staffAware = isRoleplay ? !!aiConfig.staffAware : false;
    const submitLook = isVoice ? (aiConfig.customerLook ?? null) : null;
    const hasAiCfg =
      !!submitVoice ||
      submitProfile != null ||
      submitLook != null ||
      staffAware ||
      Object.keys(legacyAiCfg).length > 0;
    onSubmit({
      name: values.name,
      difficulty: values.mode === "qa_drill" ? undefined : values.difficulty,
      mode: values.mode,
      interactionMode: isVoice ? "voice" : "text",
      description: values.description || undefined,
      maxRounds: parseInt(values.maxRounds) || 8,
      positions: values.positions || undefined,
      customerPersona: isRoleplay ? joinPersonas(personas) || undefined : undefined,
      knowledgeTags: values.knowledgeTags || undefined,
      kbWeight: values.kbWeight,
      // mode-aware:角色/语气是 roleplay 专属,退回 qa_drill 时不携带隐藏字段旧值
      customerRoleKey: isRoleplay ? values.customerRoleKey : undefined,
      customerToneKey: isRoleplay ? values.customerToneKey : undefined,
      // 声音 V2:客户口音/说话风格是 roleplay 客户声线的属性(qa_drill 没有客户)。
      // 当前系统音色不支持方言 —— 字段照存(B 批已加列),将来随克隆客户声线生效。
      voiceDialect: isRoleplay ? voiceDialect : undefined,
      voiceStyle: isRoleplay ? voiceStyle : undefined,
      status: values.status,
      productCodes,
      dimensions: dimensions.length > 0 ? dimensions : undefined,
      // staffAware 是 roleplay 专属(qa_drill 是答题,没有「客户认识你」这回事)
      aiConfig: hasAiCfg
        ? {
            ...legacyAiCfg,
            voice: submitVoice,
            ...(submitProfile != null ? { voiceProfileId: submitProfile } : {}),
            ...(submitLook != null ? { customerLook: submitLook } : {}),
            staffAware,
          }
        : undefined,
    });
  };

  // ── 子片段 ────────────────────────────────────────────────
  // 产品线收进可搜索的下拉:平铺时 15 条就占掉首屏 ~340px,把唯一必填的「场景名称」挤到折线下面,
  // 而产品线只会越来越多。收起时一行,展开带搜索。
  const selectedProductNames = products
    .filter((p) => productCodes.includes(p.code))
    .map((p) => p.name);
  const filteredProducts = productQuery.trim()
    ? products.filter((p) => p.name.toLowerCase().includes(productQuery.trim().toLowerCase()))
    : products;

  const productChips = (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">
        {isRoleplay ? "关联产品线" : "考核范围 · 产品线"}
        <span className="ml-1 font-normal">不选 = 通用</span>
      </Label>
      {products.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          暂无产品线,将用通用知识库。产品线由平台运营维护。
        </p>
      ) : (
        <details
          className="group rounded-md border bg-background"
          open={productOpen}
          onToggle={(e) => setProductOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary className="flex cursor-pointer select-none items-center justify-between gap-2 px-3 py-2 text-sm">
            <span className={selectedProductNames.length ? "" : "text-muted-foreground"}>
              {selectedProductNames.length === 0
                ? "全部产品线(通用)"
                : selectedProductNames.length <= 2
                  ? selectedProductNames.join("、")
                  : `${selectedProductNames.slice(0, 2).join("、")} 等 ${selectedProductNames.length} 个`}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t p-2">
            <Input
              className="mb-2 h-8"
              placeholder="搜索产品线…"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <div className="max-h-44 space-y-0.5 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <p className="px-1 py-2 text-xs text-muted-foreground">没有匹配的产品线</p>
              ) : (
                filteredProducts.map((p) => (
                  <label
                    key={p.code}
                    className="flex cursor-pointer select-none items-center gap-2 rounded px-1.5 py-1.5 text-sm hover:bg-muted/60"
                  >
                    <Checkbox
                      checked={productCodes.includes(p.code)}
                      onCheckedChange={() => toggleProduct(p.code)}
                    />
                    <span>{p.name}</span>
                  </label>
                ))
              )}
            </div>
            {productCodes.length > 0 && (
              <button
                type="button"
                className="mt-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setProductCodes([])}
              >
                清空已选
              </button>
            )}
          </div>
        </details>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEdit
              ? t("scenarioForm.titleEdit")
              : step === "mode"
                ? "新建陪练场景"
                : isRoleplay
                  ? "新建 · 情景对练"
                  : "新建 · 知识问答闯关"}
          </DialogTitle>
        </DialogHeader>

        {/* 第一步:选模式 */}
        {step === "mode" && !isEdit ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-2">
            <p className="text-sm text-muted-foreground">你想让员工练什么?选一种开始。</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => chooseMode("qa_drill")}
                className="group flex cursor-pointer flex-col gap-2 rounded-xl border-2 border-muted p-5 text-left transition-colors hover:border-primary hover:bg-primary/5"
              >
                <GraduationCap className="h-7 w-7 text-primary" />
                <span className="text-base font-semibold">知识问答闯关</span>
                <span className="text-sm text-muted-foreground">
                  AI 从知识库出题,员工逐题作答、自动打分。
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  适合:背产品知识 / 话术考核
                </span>
              </button>
              <button
                type="button"
                onClick={() => chooseMode("roleplay")}
                className="group flex cursor-pointer flex-col gap-2 rounded-xl border-2 border-muted p-5 text-left transition-colors hover:border-primary hover:bg-primary/5"
              >
                <MessagesSquare className="h-7 w-7 text-primary" />
                <span className="text-base font-semibold">情景对练</span>
                <span className="text-sm text-muted-foreground">
                  AI 扮演客户多轮对话,练应对、异议处理、成交推进。
                </span>
                <span className="mt-1 text-xs text-muted-foreground">适合:实战演练 / 异议处理</span>
              </button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 pb-2">
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => setStep("mode")}
                    className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> 重选训练模式
                  </button>
                )}

                {/* ROLEPLAY: AI 一键生成 */}
                {isRoleplay && !isEdit && (
                  <div className="space-y-2.5 rounded-lg border border-primary/40 bg-primary/5 p-3.5">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <Label className="text-sm font-semibold">
                        AI 一键生成场景(推荐 · 填得最少)
                      </Label>
                    </div>
                    {productChips}
                    <div className="flex items-end gap-2">
                      <div className="flex-1 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">场景目的</Label>
                        <select
                          aria-label="场景目的"
                          className={`${selectCls} bg-background`}
                          value={scenarioGoal}
                          onChange={(e) => setScenarioGoal(e.target.value)}
                        >
                          {SCENARIO_GOALS.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        type="button"
                        className="gap-1"
                        disabled={generatingScenario}
                        onClick={handleGenerateScenario}
                      >
                        {generatingScenario ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        生成场景
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      AI 据所选产品线的知识库起草「名称 / 背景 / 客户人设 /
                      评估维度」,生成后可继续修改。
                    </p>
                  </div>
                )}

                {/* 名称 */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel>{t("scenarioForm.name")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("scenarioForm.namePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* QA_DRILL: 考核范围 + 题量 + 难度 */}
                {!isRoleplay && (
                  <>
                    {productChips}
                    <FormField
                      control={form.control}
                      name="knowledgeTags"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5">
                          <FormLabel>知识库标签(可选,缩小到某类知识)</FormLabel>
                          <FormControl>
                            {/* 可用度模式:按所选产品线标出每个标签下的已审内容数,零的置灰 ——
                                此前产品线和标签交集为空时 ai 静默忽略标签出题,这里配的时候就看不出来 */}
                            <TagSelector
                              value={field.value}
                              onChange={field.onChange}
                              availability={{
                                productCodes,
                                minCount: parseInt(form.watch("maxRounds")) || 8,
                              }}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            系统从所选范围的知识库自动抽题（最多 {form.watch("maxRounds") || 8}{" "}
                            题）;<b>产品线 / 标签都不选 = 从全部知识库通用抽题</b>，无需手写题目。
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <FormField
                        control={form.control}
                        name="maxRounds"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FormLabel>题目数量</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="difficulty"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FormLabel>{t("scenarioForm.difficulty")}</FormLabel>
                            <FormControl>
                              <select className={selectCls} {...field}>
                                <option value="easy">{t("scenarioForm.difficultyEasy")}</option>
                                <option value="medium">{t("scenarioForm.difficultyMedium")}</option>
                                <option value="hard">{t("scenarioForm.difficultyHard")}</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FormLabel>{t("scenarioForm.status")}</FormLabel>
                            <FormControl>
                              <select className={selectCls} {...field}>
                                <option value="draft">{t("scenarioForm.statusDraft")}</option>
                                <option value="active">{t("scenarioForm.statusActive")}</option>
                                <option value="archived">{t("scenarioForm.statusArchived")}</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}

                {/* ROLEPLAY: 难度/轮数/状态 + 人设卡片 + 高级 */}
                {isRoleplay && (
                  <>
                    {/* 编辑态没有上方 AI 面板,产品线在这里选;新建态在 AI 面板里已选 */}
                    {isEdit && productChips}

                    {/* V29 语音陪练:对练形式(场景级拍板,员工端不可切换)。语音 = H5 全语音半双工。 */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium">对练形式</Label>
                      <div className="flex gap-2">
                        {[
                          { key: "text", label: "文本对练", hint: "员工打字回复" },
                          { key: "voice", label: "语音对练", hint: "员工开口说,AI 客户有声回应" },
                        ].map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setInteractionMode(opt.key)}
                            className={`flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                              interactionMode === opt.key
                                ? "border-primary bg-primary/5 text-foreground"
                                : "border-input bg-background text-muted-foreground hover:border-primary/50"
                            }`}
                          >
                            <span className="flex items-center gap-1.5 font-medium">
                              {opt.key === "voice" ? (
                                <Mic className="h-3.5 w-3.5" />
                              ) : (
                                <MessagesSquare className="h-3.5 w-3.5" />
                              )}
                              {opt.label}
                            </span>
                            <span className="text-xs text-muted-foreground">{opt.hint}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {interactionMode === "voice" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                          客户音色(AI 客户说话的声音,按客户画像选)
                        </Label>
                        <div className="flex gap-2">
                          {/* 0908 老板「弹框用系统默认」:与同屏新加的口音/风格 shadcn Select
                              并排的这只原生 select 最扎眼,一并换掉(值语义不变)。 */}
                          <Select value={voiceValue} onValueChange={onVoiceChange}>
                            <SelectTrigger aria-label="客户音色" className="h-9 flex-1 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {voices.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.label} — {v.description}
                                </SelectItem>
                              ))}
                              {myVoices.map((p) => (
                                <SelectItem key={p.id} value={`${MY_VOICE_PREFIX}${p.id}`}>
                                  {p.label} — 我的音色(分身舱),可设方言
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 shrink-0 gap-1"
                            disabled={previewingVoice || (voices.length === 0 && !usingMyVoice)}
                            onClick={handlePreviewVoice}
                          >
                            {previewingVoice ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Volume2 className="h-3.5 w-3.5" />
                            )}
                            试听
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* 声音 V2:客户口音 / 说话风格。1024:方言 / 风格只对分身舱「我的音色」(克隆声线)生效 ——
                        预置音色是系统音色,不认方言指令(0926 修复波因此下架过方言)。选了「我的音色」才放开;
                        预置音色时锁住并一句话说清。Radix SelectItem 不收空串,null 用哨兵值映射。
                        旧场景存过的口音照旧回显,可改回普通话,不静默洗掉。 */}
                    {interactionMode === "voice" && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="scenario-voice-dialect" className="text-xs">
                            客户口音
                          </Label>
                          <Select
                            disabled={!usingMyVoice && voiceDialect == null}
                            value={voiceDialect ?? "__mandarin__"}
                            onValueChange={(v) => setVoiceDialect(v === "__mandarin__" ? null : v)}
                          >
                            <SelectTrigger
                              id="scenario-voice-dialect"
                              aria-label="客户口音"
                              className="h-9 shadow-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__mandarin__">普通话(默认)</SelectItem>
                              {DH_VOICE_DIALECTS.filter(
                                (d) => usingMyVoice || d.value === voiceDialect
                              ).map((d) => (
                                <SelectItem key={d.value} value={d.value}>
                                  {usingMyVoice ? d.label : `${d.label}(预置音色不生效)`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="scenario-voice-style" className="text-xs">
                            说话风格(可选)
                          </Label>
                          <Select
                            disabled={!usingMyVoice}
                            value={voiceStyle ?? "__none__"}
                            onValueChange={(v) => setVoiceStyle(v === "__none__" ? null : v)}
                          >
                            <SelectTrigger
                              id="scenario-voice-style"
                              aria-label="说话风格"
                              className="h-9 shadow-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">不指定</SelectItem>
                              {DH_VOICE_STYLES.map((st) => (
                                <SelectItem key={st.value} value={st.value}>
                                  {/* ★1006:数字人线把 natural 改名「原声风格/最像本人」(克隆声纹语境);
                                      陪练里是 AI 扮客户的说话风格 —— 这里保留原说法。 */}
                                  {st.value === "natural"
                                    ? "自然对话 — 像面对面聊天,不端着"
                                    : `${st.label} — ${st.desc}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* 1025:语音页客户头像男 / 女。自动 = 预置音色按男女声、我的音色按人设称谓推断 */}
                        <div className="space-y-1.5">
                          <Label htmlFor="scenario-customer-look" className="text-xs">
                            客户头像
                          </Label>
                          <Select
                            value={aiConfig.customerLook ?? "__auto__"}
                            onValueChange={(v) =>
                              setAiConfig((c) => ({
                                ...c,
                                customerLook: v === "male" || v === "female" ? v : null,
                              }))
                            }
                          >
                            <SelectTrigger
                              id="scenario-customer-look"
                              aria-label="客户头像"
                              className="h-9 shadow-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__auto__">自动(跟随音色)</SelectItem>
                              <SelectItem value="male">男性客户</SelectItem>
                              <SelectItem value="female">女性客户</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="col-span-2 -mt-1 text-2xs text-muted-foreground">
                          {usingMyVoice
                            ? "用你在分身舱录的声音扮客户,可以说方言、换说话风格。"
                            : "预置音色只说普通话。要方言,或用老板 / 同事本人的声音扮客户,请选「我的音色」(先在分身舱录一段)。"}
                        </p>
                      </div>
                    )}

                    {/* V2 L4:AI 认得出员工 —— 只有老客户回访/售后/内部演练该开,默认关 */}
                    <label className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!!aiConfig.staffAware}
                        onChange={(e) =>
                          setAiConfig((c) => ({ ...c, staffAware: e.target.checked }))
                        }
                      />
                      <span className="text-sm">
                        AI 客户认识这名员工
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          开了之后 AI 开场可能直接叫出员工姓名(「小李啊，上次说的那个…」)。
                          只适合老客户回访、售后、内部演练 —— 陌生客户场景请保持关闭。
                        </span>
                      </span>
                    </label>

                    {/* 首屏只留「难度」:轮数和状态都是低频项,已收进高级设置;
                        状态更是只在编辑时才有意义(新建一律 active,不必问)。 */}
                    <FormField
                      control={form.control}
                      name="difficulty"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5">
                          <FormLabel>{t("scenarioForm.difficulty")}</FormLabel>
                          <FormControl>
                            <select className={selectCls} {...field}>
                              <option value="easy">{t("scenarioForm.difficultyEasy")}</option>
                              <option value="medium">{t("scenarioForm.difficultyMedium")}</option>
                              <option value="hard">{t("scenarioForm.difficultyHard")}</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* 客户人设卡片 */}
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Label className="text-sm font-medium">客户人设</Label>
                        <span className="text-xs text-muted-foreground">
                          AI 扮演的客户,可多个,陪练时轮流出场
                        </span>
                        <div className="ml-auto flex items-center gap-1.5">
                          <Label htmlFor="persona-count" className="text-xs text-muted-foreground">
                            生成
                          </Label>
                          <select
                            id="persona-count"
                            aria-label="生成人设数量"
                            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                            value={personaCount}
                            onChange={(e) => setPersonaCount(parseInt(e.target.value))}
                          >
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                          <span className="text-xs text-muted-foreground">个</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1 text-xs"
                            disabled={generatingPersonas}
                            onClick={handleGeneratePersonas}
                          >
                            {generatingPersonas ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Sparkles className="h-3.5 w-3.5" />
                            )}
                            AI 生成
                          </Button>
                        </div>
                      </div>

                      {generatingPersonas ? (
                        <div className="space-y-2">
                          {Array.from({ length: personaCount }).map((_, i) => (
                            <div
                              key={i}
                              className="h-24 animate-pulse rounded-lg border bg-muted/40"
                            />
                          ))}
                        </div>
                      ) : personas.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                          还没有客户人设。点「AI 生成」让 AI 据产品知识起草 {personaCount}{" "}
                          个不同类型的客户, 或手动添加。留空也行,陪练会用默认客户。
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {personas.map((p, i) => {
                            const len = p.trim().length;
                            return (
                              <div
                                key={i}
                                className="space-y-1.5 rounded-lg border bg-card p-3 shadow-sm"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                    客户 {i + 1}
                                  </span>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    aria-label={`删除客户 ${i + 1}`}
                                    onClick={() => removePersona(i)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                <Textarea
                                  rows={4}
                                  placeholder="客户所在行业 / 角色、痛点、对产品的核心顾虑、决策关注点…"
                                  value={p}
                                  onChange={(e) => updatePersona(i, e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground">
                                  <span className={len > 0 && len < 80 ? "text-amber-500" : ""}>
                                    {len}
                                  </span>{" "}
                                  字{len > 0 && len < 80 ? " · 建议 ≥80 字更有挑战" : ""}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={addPersona}
                      >
                        <Plus className="h-4 w-4" /> 手动添加一个客户
                      </Button>
                    </div>

                    {/* 高级设置 */}
                    <details className="rounded-lg border border-dashed p-3">
                      <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                        高级设置(描述 / 轮数 / 客户类型 / 知识库标签 / 评估维度 ·
                        可选,留空用智能默认)
                      </summary>
                      <div className="mt-3 space-y-4">
                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => {
                            const len = field.value?.length ?? 0;
                            return (
                              <FormItem className="space-y-1.5">
                                <FormLabel>{t("scenarioForm.description")}</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder={t("scenarioForm.descriptionPlaceholder")}
                                    rows={3}
                                    {...field}
                                  />
                                </FormControl>
                                <p className="text-xs text-muted-foreground">{len} / 300 字</p>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <FormField
                            control={form.control}
                            name="maxRounds"
                            render={({ field }) => (
                              <FormItem className="space-y-1.5">
                                <FormLabel>{t("scenarioForm.maxRounds")}</FormLabel>
                                <FormControl>
                                  <Input type="number" min="1" max="60" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {isEdit && (
                            <FormField
                              control={form.control}
                              name="status"
                              render={({ field }) => (
                                <FormItem className="space-y-1.5">
                                  <FormLabel>{t("scenarioForm.status")}</FormLabel>
                                  <FormControl>
                                    <select className={selectCls} {...field}>
                                      <option value="draft">{t("scenarioForm.statusDraft")}</option>
                                      <option value="active">
                                        {t("scenarioForm.statusActive")}
                                      </option>
                                      <option value="archived">
                                        {t("scenarioForm.statusArchived")}
                                      </option>
                                    </select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                        <FormField
                          control={form.control}
                          name="positions"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5">
                              <FormLabel>{t("scenarioForm.positions")}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t("scenarioForm.positionsPlaceholder")}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="knowledgeTags"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5">
                              <FormLabel>{t("scenarioForm.knowledgeTags")}</FormLabel>
                              <FormControl>
                                <TagSelector value={field.value} onChange={field.onChange} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <FormField
                            control={form.control}
                            name="customerRoleKey"
                            render={({ field }) => (
                              <FormItem className="space-y-1.5">
                                <FormLabel>{t("scenarioForm.customerRole")}</FormLabel>
                                <FormControl>
                                  <select
                                    className={selectCls}
                                    value={field.value ?? ""}
                                    onBlur={field.onBlur}
                                    onChange={(e) => field.onChange(e.target.value || null)}
                                  >
                                    <option value="">
                                      {t("scenarioForm.useDefault", {
                                        key: customerRoles?.defaultKey ?? "hesitant_buyer",
                                      })}
                                    </option>
                                    {customerRoles?.presets.map((p) => (
                                      <option key={p.key} value={p.key}>
                                        {p.name} — {p.description}
                                      </option>
                                    ))}
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="customerToneKey"
                            render={({ field }) => (
                              <FormItem className="space-y-1.5">
                                <FormLabel>{t("scenarioForm.customerTone")}</FormLabel>
                                <FormControl>
                                  <select
                                    className={selectCls}
                                    value={field.value ?? ""}
                                    onBlur={field.onBlur}
                                    onChange={(e) => field.onChange(e.target.value || null)}
                                  >
                                    <option value="">
                                      {t("scenarioForm.useDefault", {
                                        key: customerTones?.defaultKey ?? "professional",
                                      })}
                                    </option>
                                    {customerTones?.presets
                                      .filter((tone) => !selectedRoleIncompat?.includes(tone.key))
                                      .map((tone) => (
                                        <option key={tone.key} value={tone.key}>
                                          {tone.name} — {tone.description}
                                        </option>
                                      ))}
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={form.control}
                          name="kbWeight"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5">
                              <FormLabel>{t("scenarioForm.kbWeight")}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.05"
                                  min="0"
                                  max="1"
                                  value={field.value}
                                  onBlur={field.onBlur}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* 评估维度 */}
                        <div className="rounded border p-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{t("scenarioForm.dimensionsHeading")}</h4>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => applyPreset("sales")}
                              >
                                {t("scenarioForm.presetSales")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => applyPreset("aftersales")}
                              >
                                {t("scenarioForm.presetAftersales")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => applyPreset("consult")}
                              >
                                {t("scenarioForm.presetConsult")}
                              </Button>
                            </div>
                          </div>
                          {dimensions.length > 0 && (
                            <table className="mt-3 w-full text-sm">
                              <thead className="text-left text-muted-foreground">
                                <tr>
                                  <th className="py-1 pr-2">{t("scenarioForm.headerKey")}</th>
                                  <th className="pr-2">{t("scenarioForm.headerLabel")}</th>
                                  <th className="w-24 pr-2">{t("scenarioForm.headerWeight")}</th>
                                  <th className="w-20 pr-2">{t("scenarioForm.headerMaxScore")}</th>
                                  <th className="w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {dimensions.map((d, i) => (
                                  <tr key={i} className="border-t">
                                    <td className="py-1 pr-2">
                                      <Input
                                        className="h-7"
                                        value={d.dimKey}
                                        onChange={(e) => updateDim(i, "dimKey", e.target.value)}
                                      />
                                    </td>
                                    <td className="pr-2">
                                      <Input
                                        className="h-7"
                                        value={d.dimLabel}
                                        onChange={(e) => updateDim(i, "dimLabel", e.target.value)}
                                      />
                                    </td>
                                    <td className="pr-2">
                                      <Input
                                        className="h-7"
                                        type="number"
                                        step="0.05"
                                        value={d.weight}
                                        onChange={(e) =>
                                          updateDim(i, "weight", parseFloat(e.target.value) || 0)
                                        }
                                      />
                                    </td>
                                    <td className="pr-2">
                                      <Input
                                        className="h-7"
                                        type="number"
                                        value={d.maxScore ?? 20}
                                        onChange={(e) =>
                                          updateDim(i, "maxScore", parseInt(e.target.value) || 20)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        aria-label="删除维度"
                                        onClick={() => removeDim(i)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <Button type="button" size="sm" variant="outline" onClick={addDim}>
                              <Plus className="mr-1 h-3 w-3" /> {t("scenarioForm.addDimension")}
                            </Button>
                            {dimensions.length > 0 && (
                              <span
                                className={`text-sm ${weightOk ? "text-success" : "text-warning"}`}
                              >
                                {t("scenarioForm.weightSumOk", { sum: weightSum.toFixed(3) })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </details>
                  </>
                )}
              </div>

              <div className="flex shrink-0 justify-end gap-2 border-t pt-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  {t("scenarioForm.cancel")}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  {isEdit ? t("scenarioForm.save") : t("scenarioForm.create")}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
